import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Modal, Spinner } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import {
  GetHomeDataResponse,
  GetAllAssistantTodosResponse,
  GetUserTodosResponse,
  GetFollowupItemsResponse,
  GetUserArtifactsResponse,
  SampleRoutine,
  AgentStreamEvents,
  AgentEvents,
} from '@dreamai/types';
import { useAgent } from '../../contexts/AgentContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useMessaging } from '../../contexts/MessagingContext';
import { useMobileNav } from '../../contexts/MobileNavContext';
import { usePusher } from '../../contexts/PusherContext';
import { useResponseArtifactEvents } from '../../providers';
import ThreadViewContainer from './sidebar/ThreadViewContainer';
import { ChatCanvasProvider } from '../../contexts/ChatCanvasContext';
import {
  ThreadCallProvider,
  useThreadCallOptional,
} from '../../contexts/ThreadCallContext';
import { ReactComponent as CallingIcon } from '../../icons/webrtc-calls/calling.svg';
import { ReactComponent as ListsIcon } from '../../icons/home/list.svg';
import api from '../../api';
import UniNav, { UniTab } from './UniNav';
import ChannelSwitcher from './ChannelSwitcher';
import { isRepresentedInWaitingForYou } from './activityHelpers';
import { onOpenChatRequest } from './chatLinks';
import WelcomeHeader from './WelcomeHeader';
import HomeMorningList from '../morning-list/HomeMorningList';
import GlobalAppsSection, { HomeLink } from './GlobalAppsSection';
import ProjectDetailView from './ProjectDetailView';
import MobileWhatsappIntroModal, {
  hasSeenMobileWhatsappIntro,
} from './MobileWhatsappIntroModal';
import RoutineCreateView from './RoutineCreateView';
import RoutineDetailView from './RoutineDetailView';
import SampleRoutineDetailView from './SampleRoutineDetailView';
import DailyBriefingDrawer from './DailyBriefingDrawer';
import ActivityPage from './ActivityPage';
import RemindersPage from './RemindersPage';
import DocumentsPage from './DocumentsPage';
import './uni-page.scss';

// Tabs that render as Global Apps (inside GlobalAppsSection) on mobile.
const MOBILE_APP_TABS: UniTab[] = ['reminders', 'documents'];

const UniPageHeaderCallButton: React.FC = () => {
  const threadCall = useThreadCallOptional();
  const { activeAgent } = useAgent();
  const { voiceCallingEnabled } = useFeatureFlags();
  if (!voiceCallingEnabled) return null;
  if (!threadCall?.canStartCall) return null;
  const label = `Call ${activeAgent?.nickname || activeAgent?.name || 'agent'}`;
  return (
    <button
      type='button'
      className='uni-page-chat-header-call-btn'
      onClick={threadCall.startCall}
      aria-label={label}
      title={label}
    >
      <CallingIcon />
    </button>
  );
};

const UniPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { activeAgent } = useAgent();
  const { messagingState } = useMessaging();
  const { isMobile } = useMobileNav();
  const { mobileScheduleHomeEnabled } = useFeatureFlags();
  const [homeData, setHomeData] = useState<GetHomeDataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<UniTab>(() => {
    // When an app is deep-linked via app_tab, the top-level tab should stay on
    // home and let GlobalAppsSection render the app. Ignore 'tab' in that case
    // and also guard against non-UniTab values injected by nested app params.
    if (searchParams.get('app_tab')) return 'home';
    const urlTab = searchParams.get('tab') as UniTab;
    const validTabs: UniTab[] = ['home', 'activity', 'reminders', 'documents'];
    if (!validTabs.includes(urlTab)) return 'home';
    // On mobile the Reminders / Documents tabs open as apps inside
    // GlobalAppsSection so they share the compact mobile app shell.
    if (isMobile && MOBILE_APP_TABS.includes(urlTab)) return 'home';
    return urlTab;
  });

  // Keep the tab in sync when the viewport crosses the mobile breakpoint: a
  // `?tab=reminders|documents` URL is owned by GlobalAppsSection on mobile and
  // by the direct tab on desktop.
  useEffect(() => {
    if (isMobile) {
      if (MOBILE_APP_TABS.includes(activeTab)) setActiveTab('home');
      return;
    }
    if (activeTab !== 'home' || searchParams.get('app_tab')) return;
    const urlTab = searchParams.get('tab') as UniTab;
    if (MOBILE_APP_TABS.includes(urlTab)) setActiveTab(urlTab);
  }, [isMobile, activeTab, searchParams]);
  const urlThreadId = useMemo(
    () => searchParams.get('cid') ?? null,
    [searchParams],
  );
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  const [showMobileIntro, setShowMobileIntro] = useState(
    () => !hasSeenMobileWhatsappIntro(),
  );
  const [channelThreadId, setChannelThreadId] = useState<string | null>(
    () => searchParams.get('cid') ?? null,
  );
  const previousAgentIdRef = useRef<string | undefined>(undefined);

  // A selected channel belongs to a single agent — clear it on agent switch,
  // but not on the initial agent load (so a channel from the URL survives mount).
  useEffect(() => {
    const currentId = activeAgent?.id;
    if (
      previousAgentIdRef.current &&
      currentId &&
      previousAgentIdRef.current !== currentId
    ) {
      setChannelThreadId(null);
    }
    previousAgentIdRef.current = currentId;
  }, [activeAgent?.id, setChannelThreadId]);

  // Persist the selected channel in the URL so it survives refreshes.
  useEffect(() => {
    if (channelThreadId && channelThreadId !== urlThreadId) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('cid', channelThreadId);
          return next;
        },
        { replace: true },
      );
    } else if (!channelThreadId && urlThreadId) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('cid');
          return next;
        },
        { replace: true },
      );
    }
  }, [channelThreadId, urlThreadId, setSearchParams]);

  // Chat links in assistant messages (dream://chat/<name>) switch the chat
  // panel to the named chat.
  useEffect(() => {
    return onOpenChatRequest(async (name) => {
      try {
        const token = await getAccessTokenSilently();
        const query = new URLSearchParams();
        if (activeAgent?.id) query.set('agent_id', activeAgent.id);
        const response = await api.get<{
          success: boolean;
          channels: { threadId: string; name: string; isMain: boolean }[];
        }>(`/chat-channels?${query.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const match = response.data.channels?.find(
          (c) => c.name.toLowerCase() === name.toLowerCase(),
        );
        if (match) {
          setChannelThreadId(match.isMain ? null : match.threadId);
        }
      } catch (err) {
        console.error('Failed to open chat from link:', err);
      }
    });
  }, [getAccessTokenSilently, activeAgent?.id]);
  const [regCodeLoading, setRegCodeLoading] = useState(false);
  const [registrationData, setRegistrationData] = useState<any>(null);
  const [regCodeError, setRegCodeError] = useState<string | null>(null);

  // Tab counts
  const [activityCount, setActivityCount] = useState(0);
  const [remindersCount, setRemindersCount] = useState(0);
  const [owedRepliesCount, setOwedRepliesCount] = useState(0);

  // True while a global app is open — hides the greeting/long-term sections so
  // the app view takes over the left panel.
  const [globalAppOpen, setGlobalAppOpen] = useState(false);

  // Nav turns into a glassy pill once the left panel scrolls
  const [navScrolled, setNavScrolled] = useState(false);
  // The left pane's scrollbar only shows while the user is actively scrolling.
  const [leftScrolling, setLeftScrolling] = useState(false);
  const leftScrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleLeftScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const scrolled = e.currentTarget.scrollTop > 8;
    setNavScrolled((prev) => (prev === scrolled ? prev : scrolled));
    setLeftScrolling(true);
    if (leftScrollTimer.current) clearTimeout(leftScrollTimer.current);
    leftScrollTimer.current = setTimeout(() => setLeftScrolling(false), 900);
  }, []);
  useEffect(
    () => () => {
      if (leftScrollTimer.current) clearTimeout(leftScrollTimer.current);
    },
    [],
  );
  const [todoistConnected, setTodoistConnected] = useState(false);

  // Left-panel view — driven by URL params so nav tabs work normally
  const currentView = searchParams.get('view');
  const creatingRoutine = currentView === 'create-routine';
  const viewingRoutineDetail = currentView === 'routine-detail';
  const viewingSampleRoutine = currentView === 'sample-routine';
  const viewingDailyBriefing = currentView === 'daily-briefing';
  const viewingProjectDetail = currentView === 'project-detail';
  const routineAutopilotId = searchParams.get('autopilot_id') || '';
  const routineNameParam = searchParams.get('routine_name') || '';
  const projectThreadId = searchParams.get('project_id') || '';
  const projectTitleParam = searchParams.get('project_title') || '';
  const cameFrom = searchParams.get('from') || '';

  // Held in state since SampleRoutine is an object (not serializable to URL params)
  const [selectedSampleRoutine, setSelectedSampleRoutine] =
    useState<SampleRoutine | null>(null);
  const [settingUp, setSettingUp] = useState(false);

  const handleTabChange = useCallback(
    (tab: UniTab) => {
      setActiveTab(tab);
      if (tab === 'home') {
        setSearchParams({});
      } else {
        setSearchParams({ tab });
      }
    },
    [setSearchParams],
  );

  const handleCreateRoutine = useCallback(() => {
    setActiveTab('home');
    setSearchParams({ view: 'create-routine' });
  }, [setSearchParams]);

  const handleCreateRoutineBack = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const handleRoutineCreated = useCallback(
    (autopilotId: string, routineName: string) => {
      setSearchParams({
        view: 'routine-detail',
        autopilot_id: autopilotId,
        routine_name: routineName,
      });
    },
    [setSearchParams],
  );

  const handleViewProject = useCallback(
    (threadId: string, title: string) => {
      setActiveTab('home');
      setSearchParams({
        view: 'project-detail',
        project_id: threadId,
        project_title: title,
      });
    },
    [setSearchParams],
  );

  const handleOpenLists = useCallback(() => {
    setActiveTab('home');
    setSearchParams({ home: 'lists' });
  }, [setSearchParams]);

  const handleProjectBack = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const homeLinks = useMemo<HomeLink[]>(
    () => [
      {
        id: 'lists',
        name: 'All Lists',
        icon: <ListsIcon />,
        onSelect: handleOpenLists,
      },
    ],
    [handleOpenLists],
  );

  const handleRoutineDetailBack = useCallback(() => {
    if (cameFrom === 'daily-briefing') {
      setSearchParams({ view: 'daily-briefing' });
    } else {
      setSearchParams({});
    }
  }, [setSearchParams, cameFrom]);

  const handleViewSampleRoutine = useCallback(
    (sampleRoutine: SampleRoutine) => {
      setSelectedSampleRoutine(sampleRoutine);
      setActiveTab('home');
      setSearchParams({ view: 'sample-routine', sample_id: sampleRoutine.id });
    },
    [setSearchParams],
  );

  const handleSampleRoutineBack = useCallback(() => {
    setSelectedSampleRoutine(null);
    setSearchParams({});
  }, [setSearchParams]);

  const handleDailyBriefingClose = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('view');
      return next;
    });
  }, [setSearchParams]);

  const handleViewRoutineFromBriefing = useCallback(
    (autopilotId: string, routineName: string) => {
      setActiveTab('home');
      setSearchParams({
        view: 'routine-detail',
        autopilot_id: autopilotId,
        routine_name: routineName,
        from: 'daily-briefing',
      });
    },
    [setSearchParams],
  );

  const handleSetUpSampleRoutine = useCallback(
    async (sampleRoutine: SampleRoutine) => {
      setSettingUp(true);
      try {
        const token = await getAccessTokenSilently();
        await api.post(
          `/sample-routines/${sampleRoutine.id}/setup`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        // Stay on the page — the AI response will appear in the chat panel via Pusher.
        // settingUp remains true to keep the button disabled until the user navigates away.
      } catch (err) {
        console.error('Failed to set up sample routine:', err);
        setSettingUp(false);
      }
    },
    [getAccessTokenSilently, setSearchParams],
  );

  // Close modal when WhatsApp becomes connected
  useEffect(() => {
    if (messagingState.whatsapp.connected && showWhatsappModal) {
      setShowWhatsappModal(false);
    }
  }, [messagingState.whatsapp.connected, showWhatsappModal]);

  const fetchHomeData = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await api.get<GetHomeDataResponse>('/home/data', {
        params: activeAgent?.id ? { agent_id: activeAgent.id } : undefined,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setHomeData(response.data);
      }
    } catch (err) {
      console.error('Error fetching home data:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, activeAgent?.id]);

  useEffect(() => {
    fetchHomeData();
  }, [fetchHomeData]);

  // ─── Route research_app_id to the right surface ──
  // InlineResponseArtifact sets app_tab=documents + research_app_id. In the
  // legacy /conversations route CanvasAssets consumes app_tab; in UniPage we
  // want it to open the Documents app (GlobalAppsSection) instead of the
  // direct documents tab.
  useEffect(() => {
    const researchAppId = searchParams.get('research_app_id');
    const appTab = searchParams.get('app_tab');
    if (!researchAppId) return;
    if (appTab === 'documents' && activeTab !== 'home') {
      setActiveTab('home');
    } else if (appTab !== 'documents' && activeTab !== 'documents') {
      setActiveTab('documents');
    }
  }, [searchParams, activeTab]);

  // ─── Fetch tab counts ─────────────────────────────────────────

  // Activity count = in-flight todos + pending artifacts
  const fetchActivityCount = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };

      const [todosRes, artifactsRes] = await Promise.all([
        api.get<GetAllAssistantTodosResponse>('/todos', { headers }),
        api.get<GetUserArtifactsResponse>(
          '/response-artifacts/user/artifacts?status=pending',
          { headers },
        ),
      ]);
      const activeTodos = (todosRes.data.todos || []).filter(
        (t) => !t.isArchivedThread && !isRepresentedInWaitingForYou(t),
      );
      const pendingArtifacts = (artifactsRes.data.artifacts || []).filter(
        (a) => a.artifactType !== 'blank_todo',
      );
      setActivityCount(activeTodos.length + pendingArtifacts.length);
    } catch (err) {
      console.error('Error fetching activity count:', err);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchActivityCount();
  }, [fetchActivityCount]);

  // Debounced refresh — Pusher events can arrive in bursts
  const activityRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const refreshActivityCount = useCallback(() => {
    if (activityRefreshTimerRef.current) {
      clearTimeout(activityRefreshTimerRef.current);
    }
    activityRefreshTimerRef.current = setTimeout(() => {
      activityRefreshTimerRef.current = null;
      fetchActivityCount();
    }, 500);
  }, [fetchActivityCount]);

  useEffect(() => {
    return () => {
      if (activityRefreshTimerRef.current) {
        clearTimeout(activityRefreshTimerRef.current);
      }
    };
  }, []);

  // Pusher: sub-thread (in-flight) events
  const pusherCtx = usePusher(true) as any;
  const pusherChannel = pusherCtx?.pusherChannel ?? null;

  useEffect(() => {
    if (!pusherChannel) return;
    pusherChannel.bind(
      AgentStreamEvents.SUB_THREAD_CREATED,
      refreshActivityCount,
    );
    pusherChannel.bind(
      AgentStreamEvents.SUB_THREAD_UPDATE,
      refreshActivityCount,
    );
    return () => {
      pusherChannel.unbind(
        AgentStreamEvents.SUB_THREAD_CREATED,
        refreshActivityCount,
      );
      pusherChannel.unbind(
        AgentStreamEvents.SUB_THREAD_UPDATE,
        refreshActivityCount,
      );
    };
  }, [pusherChannel, refreshActivityCount]);

  // Pusher: artifact (waiting-for-you) events
  const {
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
  } = useResponseArtifactEvents();

  useEffect(() => {
    const cbId = 'uni-page-activity-count';
    registerCreatedCallback(cbId, refreshActivityCount);
    registerUpdatedCallback(cbId, refreshActivityCount);
    return () => {
      unregisterCreatedCallback(cbId);
      unregisterUpdatedCallback(cbId);
    };
  }, [
    registerCreatedCallback,
    unregisterCreatedCallback,
    registerUpdatedCallback,
    unregisterUpdatedCallback,
    refreshActivityCount,
  ]);

  const fetchRemindersCount = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };

      // Reminders count (only if not managed by Todoist)
      const [userTodosRes, todoistRes] = await Promise.all([
        api.get<GetUserTodosResponse>(
          `/user-todos?assigneeType=user${activeAgent?.id ? `&agentId=${activeAgent.id}` : ''}`,
          { headers },
        ),
        api.get<{ linkedAccounts: { id: number }[] }>('/todoist-link', {
          headers,
        }),
      ]);
      const isTodoistConnected = todoistRes.data.linkedAccounts.length > 0;
      setTodoistConnected(isTodoistConnected);
      if (!isTodoistConnected) {
        const activeTodosCount = (userTodosRes.data.todos || []).filter(
          (t) => t.status !== 'complete',
        ).length;
        setRemindersCount(activeTodosCount);
      } else {
        setRemindersCount(0);
      }
    } catch (err) {
      console.error('Error fetching reminders count:', err);
    }
  }, [getAccessTokenSilently, activeAgent?.id]);

  useEffect(() => {
    fetchRemindersCount();
  }, [fetchRemindersCount]);

  const fetchOwedRepliesCount = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<GetFollowupItemsResponse>(
        '/followup-items',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOwedRepliesCount((response.data.openItems || []).length);
    } catch (err) {
      console.error('Error fetching owed replies count:', err);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchOwedRepliesCount();
  }, [fetchOwedRepliesCount]);

  // Refresh the badge when a global app closes so counts changed inside the
  // Owed replies app (clears/undos) are reflected on the tile immediately.
  const handleGlobalAppOpenChange = useCallback(
    (open: boolean) => {
      setGlobalAppOpen(open);
      if (!open) fetchOwedRepliesCount();
    },
    [fetchOwedRepliesCount],
  );

  // Keep the reminders badge live as todos are created/completed/dismissed.
  const userChannel = pusherCtx?.userChannel ?? null;
  useEffect(() => {
    if (!userChannel) return;
    userChannel.bind(AgentEvents.USER_TODO_UPDATED, fetchRemindersCount);
    userChannel.bind(
      AgentEvents.FOLLOWUP_SWEEP_COMPLETED,
      fetchOwedRepliesCount,
    );
    return () => {
      userChannel.unbind(AgentEvents.USER_TODO_UPDATED, fetchRemindersCount);
      userChannel.unbind(
        AgentEvents.FOLLOWUP_SWEEP_COMPLETED,
        fetchOwedRepliesCount,
      );
    };
  }, [userChannel, fetchRemindersCount, fetchOwedRepliesCount]);

  // An open app on mobile brings its own header (MobileAppHeader), so the
  // site nav is dropped and the pane goes edge to edge.
  const mobileAppShell = isMobile && activeTab === 'home' && globalAppOpen;

  if (isMobile && mobileScheduleHomeEnabled) {
    return (
      <div className='uni-page uni-page--mobile-home'>
        {showMobileIntro && (
          <MobileWhatsappIntroModal onClose={() => setShowMobileIntro(false)} />
        )}
        <HomeMorningList />
      </div>
    );
  }

  return (
    <div className='uni-page'>
      {isMobile && showMobileIntro && (
        <MobileWhatsappIntroModal onClose={() => setShowMobileIntro(false)} />
      )}
      <div
        className={`uni-page-left${mobileAppShell ? ' uni-page-left--mobile-app' : ''}${leftScrolling ? ' uni-page-left--scrolling' : ''}`}
        onScroll={handleLeftScroll}
      >
        {!mobileAppShell && (
          <UniNav
            activeTab={activeTab}
            onTabChange={handleTabChange}
            activityCount={activityCount}
            remindersCount={remindersCount}
            scrolled={navScrolled}
          />
        )}
        {activeTab === 'home' && creatingRoutine ? (
          <RoutineCreateView
            onBack={handleCreateRoutineBack}
            onRoutineCreated={handleRoutineCreated}
          />
        ) : activeTab === 'home' &&
          viewingRoutineDetail &&
          routineAutopilotId ? (
          <RoutineDetailView
            autopilotId={routineAutopilotId}
            routineName={routineNameParam}
            onBack={handleRoutineDetailBack}
          />
        ) : activeTab === 'home' && viewingProjectDetail && projectThreadId ? (
          <ProjectDetailView
            threadId={projectThreadId}
            projectTitle={projectTitleParam}
            onBack={handleProjectBack}
            onArchived={handleProjectBack}
          />
        ) : activeTab === 'home' &&
          viewingSampleRoutine &&
          selectedSampleRoutine ? (
          <SampleRoutineDetailView
            sampleRoutine={selectedSampleRoutine}
            onBack={handleSampleRoutineBack}
            onSetUp={handleSetUpSampleRoutine}
            settingUp={settingUp}
            onRoutineCreated={handleRoutineCreated}
          />
        ) : (
          <>
            {activeTab === 'home' && isMobile && !globalAppOpen && (
              <WelcomeHeader />
            )}
            {activeTab === 'home' && !isMobile && !globalAppOpen && (
              <HomeMorningList embedded />
            )}
            {activeTab === 'home' && (
              <GlobalAppsSection
                presentation={isMobile ? 'grid' : 'none'}
                extraLinks={homeLinks}
                onAppOpenChange={handleGlobalAppOpenChange}
                remindersCount={remindersCount}
                owedRepliesCount={owedRepliesCount}
              />
            )}
            {activeTab === 'activity' && (
              <ActivityPage onActivityChanged={fetchActivityCount} />
            )}
            {activeTab === 'reminders' && <RemindersPage />}
            {activeTab === 'documents' && <DocumentsPage />}
          </>
        )}
      </div>
      <DailyBriefingDrawer
        open={viewingDailyBriefing}
        onClose={handleDailyBriefingClose}
        onViewRoutine={handleViewRoutineFromBriefing}
      />
      <div className='uni-page-right'>
        <ThreadCallProvider>
          <header className='uni-page-chat-header'>
            <div className='uni-page-chat-header-left'>
              <span className='uni-page-chat-header-name'>
                {activeAgent?.nickname || activeAgent?.name}
              </span>
            </div>
            <div className='uni-page-chat-header-right'>
              <UniPageHeaderCallButton />
              <ChannelSwitcher
                threadId={channelThreadId ?? homeData?.threadId ?? null}
                onSelectChannel={setChannelThreadId}
              />
            </div>
          </header>

          {loading ? (
            <div className='uni-page-loading'>Loading...</div>
          ) : homeData ? (
            <ChatCanvasProvider>
              <ThreadViewContainer
                key={channelThreadId ?? homeData.threadId}
                threadId={channelThreadId ?? homeData.threadId}
                agentId={homeData.agentId}
              />
            </ChatCanvasProvider>
          ) : null}
        </ThreadCallProvider>

        {/* WhatsApp Registration Code Modal */}
        <Modal
          show={showWhatsappModal}
          onHide={() => setShowWhatsappModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Connect WhatsApp</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className='whatsapp-reg-modal'>
              {regCodeLoading ? (
                <div className='whatsapp-reg-loading'>
                  <Spinner animation='border' size='sm' />
                  <p>Generating registration code...</p>
                </div>
              ) : regCodeError ? (
                <div className='whatsapp-reg-error'>{regCodeError}</div>
              ) : registrationData ? (
                <div className='whatsapp-reg-content'>
                  <QRCodeSVG value={registrationData.whatsapp_url} size={200} />
                  <p className='whatsapp-reg-code'>
                    Registration Code: <strong>{registrationData.code}</strong>
                  </p>
                  <p className='whatsapp-reg-instructions'>
                    Scan this QR code with your phone to open WhatsApp and send
                    the registration code. Or manually send the code to our
                    WhatsApp number.
                  </p>
                  <p className='whatsapp-reg-expiry'>
                    Code expires in 24 hours.
                  </p>
                </div>
              ) : null}
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default UniPage;
